// Copied from platform-app components/pos/receipt/SlotEditor.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ReceiptBlock } from '@platform/shared-types';
import BlockCard from './BlockCard';
import AddBlockMenu from './AddBlockMenu';

interface Props {
  title: string;
  subtitle?: string;
  blocks: ReceiptBlock[];
  onChange: (next: ReceiptBlock[]) => void;
  totalBlocksAcrossSlots: number;
  maxBlocks: number;
}

export default function SlotEditor({ title, subtitle, blocks, onChange, totalBlocksAcrossSlots, maxBlocks }: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onChange(arrayMove(blocks, oldIdx, newIdx));
  };

  const replaceBlock = (id: string, next: ReceiptBlock) =>
    onChange(blocks.map((b) => (b.id === id ? next : b)));

  const removeBlock = (id: string) =>
    onChange(blocks.filter((b) => b.id !== id));

  const atCap = totalBlocksAcrossSlots >= maxBlocks;

  return (
    <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-stone-800">
          {title}{subtitle && <span className="text-stone-400 font-normal"> — {subtitle}</span>}
        </h3>
        <AddBlockMenu
          disabled={atCap}
          onAdd={(b) => onChange([...blocks, b])}
        />
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {blocks.map((b) => (
              <BlockCard
                key={b.id}
                block={b}
                onChange={(next) => replaceBlock(b.id, next)}
                onDelete={() => removeBlock(b.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {blocks.length === 0 && (
        <div className="text-xs text-stone-400 italic mt-2">Empty — this section will be skipped on print.</div>
      )}
    </div>
  );
}
